import React, {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';
import { cn } from '@thingslog/ui-components';

type PDFDocumentProxy = Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']>;
type PDFPageProxy = Awaited<ReturnType<PDFDocumentProxy['getPage']>>;
type PDFLoadingTask = ReturnType<typeof pdfjsLib.getDocument>;
type PDFRenderTask = ReturnType<PDFPageProxy['render']>;

export interface PdfViewerRef {
  goToPage: (page: number) => void;
  prevPage: () => void;
  nextPage: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  getCurrentPage: () => number;
  getTotalPages: () => number;
  getZoom: () => number;
}

export type PdfScaleMode = 'fit-container' | 'fit-width';

export interface PdfViewerProps {
  src: string;
  page?: number;
  scaleMode?: PdfScaleMode;
  transitionDuration?: number;
  httpHeaders?: Record<string, string>;
  onPageChange?: (page: number, totalPages: number) => void;
  onZoomChange?: (zoom: number) => void;
  onLoad?: (totalPages: number) => void;
  onError?: (error: Error) => void;
  className?: string;
}

export const PDF_MIN_ZOOM = 1;
export const PDF_MAX_ZOOM = 2.5;
export const PDF_ZOOM_STEP = 0.25;

const EMPTY_PAGE_SIZE = { width: 0, height: 0 };

const clampZoom = (zoom: number): number => Math.min(PDF_MAX_ZOOM, Math.max(PDF_MIN_ZOOM, zoom));

export const PdfViewer = forwardRef<PdfViewerRef, PdfViewerProps>(function PdfViewer(
  {
    src,
    page: controlledPage,
    scaleMode = 'fit-container',
    transitionDuration = 200,
    httpHeaders,
    onPageChange,
    onZoomChange,
    onLoad,
    onError,
    className,
  }: PdfViewerProps,
  ref: React.ForwardedRef<PdfViewerRef>
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvas1Ref = useRef<HTMLCanvasElement>(null);
  const canvas2Ref = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<PDFRenderTask | null>(null);
  const renderLockRef = useRef<Promise<void> | null>(null);
  const loadingTaskRef = useRef<PDFLoadingTask | null>(null);
  const activeCanvasRef = useRef<1 | 2>(1);
  const renderVersionRef = useRef(0);
  const resizeAnimationFrameRef = useRef<number | null>(null);
  const resizeDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  const onPageChangeRef = useRef(onPageChange);
  const onZoomChangeRef = useRef(onZoomChange);
  const httpHeadersRef = useRef(httpHeaders);

  onLoadRef.current = onLoad;
  onErrorRef.current = onError;
  onPageChangeRef.current = onPageChange;
  onZoomChangeRef.current = onZoomChange;

  const httpHeadersKey = useMemo((): string => {
    return httpHeaders ? JSON.stringify(httpHeaders) : '';
  }, [httpHeaders]);

  useEffect((): void => {
    httpHeadersRef.current = httpHeaders;
  }, [httpHeadersKey, httpHeaders]);

  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [internalPage, setInternalPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [pageSize, setPageSize] = useState(EMPTY_PAGE_SIZE);
  const [visibleCanvas, setVisibleCanvas] = useState<1 | 2>(1);
  const [zoom, setZoom] = useState(PDF_MIN_ZOOM);

  const resetScrollPosition = useCallback((): void => {
    containerRef.current?.scrollTo({ top: 0, left: 0 });
  }, []);

  const currentPage = controlledPage ?? internalPage;

  useEffect((): (() => void) | void => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
      const entry = entries[0];
      if (entry) {
        const nextSize = {
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        };

        if (resizeAnimationFrameRef.current !== null) {
          cancelAnimationFrame(resizeAnimationFrameRef.current);
        }

        resizeAnimationFrameRef.current = requestAnimationFrame((): void => {
          setContainerSize((size: { width: number; height: number }) => {
            if (size.width === nextSize.width && size.height === nextSize.height) {
              return size;
            }

            return nextSize;
          });
          resizeAnimationFrameRef.current = null;
        });
      }
    });

    observer.observe(container);

    return (): void => {
      observer.disconnect();

      if (resizeAnimationFrameRef.current !== null) {
        cancelAnimationFrame(resizeAnimationFrameRef.current);
        resizeAnimationFrameRef.current = null;
      }
    };
  }, []);

  useEffect((): (() => void) => {
    if (loadingTaskRef.current) {
      loadingTaskRef.current.destroy();
      loadingTaskRef.current = null;
    }

    setPdfDoc(null);
    setTotalPages(0);
    setInternalPage(1);
    setPageSize(EMPTY_PAGE_SIZE);
    setZoom(PDF_MIN_ZOOM);

    const loadingTask = pdfjsLib.getDocument({
      url: src,
      ...(httpHeadersRef.current && { httpHeaders: httpHeadersRef.current }),
    });

    loadingTaskRef.current = loadingTask;

    loadingTask.promise
      .then((pdf: PDFDocumentProxy): void => {
        if (loadingTaskRef.current !== loadingTask) {
          return;
        }

        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        onLoadRef.current?.(pdf.numPages);
        loadingTaskRef.current = null;
      })
      .catch((error: unknown): void => {
        if (loadingTaskRef.current !== loadingTask) {
          return;
        }

        onErrorRef.current?.(
          error instanceof Error ? error : new Error('Failed to load PDF document')
        );
        loadingTaskRef.current = null;
      });

    return (): void => {
      if (loadingTaskRef.current === loadingTask) {
        loadingTask.destroy();
        loadingTaskRef.current = null;
      }
    };
  }, [src, httpHeadersKey]);

  const renderPage = useCallback((): void => {
    if (!pdfDoc || containerSize.width === 0 || containerSize.height === 0) {
      return;
    }

    const renderVersion = renderVersionRef.current + 1;
    renderVersionRef.current = renderVersion;
    const previousRender = renderLockRef.current;

    const runRender = async (): Promise<void> => {
      if (previousRender) {
        try {
          await previousRender;
        } catch {
          // The previous render reports its own error; this render only needs the canvas lock.
        }

        if (renderVersionRef.current !== renderVersion) {
          return;
        }
      }

      if (!pdfDoc || containerSize.width === 0 || containerSize.height === 0) {
        return;
      }

      if (resizeDebounceTimeoutRef.current) {
        clearTimeout(resizeDebounceTimeoutRef.current);
        resizeDebounceTimeoutRef.current = null;
      }

      const targetCanvasNum: 1 | 2 = activeCanvasRef.current === 1 ? 2 : 1;
      const targetCanvas = targetCanvasNum === 1 ? canvas1Ref.current : canvas2Ref.current;

      if (!targetCanvas) {
        return;
      }

      let currentRenderTask: PDFRenderTask | null = null;

      try {
        const page: PDFPageProxy = await pdfDoc.getPage(currentPage);

        if (renderVersionRef.current !== renderVersion) {
          return;
        }

        const context = targetCanvas.getContext('2d');

        if (!context) {
          throw new Error('Failed to acquire PDF canvas context');
        }

        const viewport = page.getViewport({ scale: 1 });
        const scaleX = containerSize.width / viewport.width;
        const scaleY = containerSize.height / viewport.height;
        const baseScale = scaleMode === 'fit-width' ? scaleX : Math.min(scaleX, scaleY);
        const scale = baseScale * zoom;
        const scaledViewport = page.getViewport({ scale });

        targetCanvas.width = scaledViewport.width;
        targetCanvas.height = scaledViewport.height;

        const renderTask = page.render({
          canvasContext: context,
          viewport: scaledViewport,
        });

        currentRenderTask = renderTask;
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        if (renderTaskRef.current === renderTask) {
          renderTaskRef.current = null;
        }

        if (renderVersionRef.current !== renderVersion) {
          return;
        }

        setPageSize((size: { width: number; height: number }) => {
          if (size.width === scaledViewport.width && size.height === scaledViewport.height) {
            return size;
          }

          return {
            width: scaledViewport.width,
            height: scaledViewport.height,
          };
        });
        activeCanvasRef.current = targetCanvasNum;
        setVisibleCanvas(targetCanvasNum);

        resizeDebounceTimeoutRef.current = setTimeout((): void => {
          const hiddenCanvas = targetCanvasNum === 1 ? canvas2Ref.current : canvas1Ref.current;
          if (hiddenCanvas) {
            hiddenCanvas.width = scaledViewport.width;
            hiddenCanvas.height = scaledViewport.height;
          }
          resizeDebounceTimeoutRef.current = null;
        }, 1000);
      } catch (error: unknown) {
        if (currentRenderTask && renderTaskRef.current === currentRenderTask) {
          renderTaskRef.current = null;
        }

        if (error instanceof Error && error.name === 'RenderingCancelledException') {
          return;
        }

        onErrorRef.current?.(
          error instanceof Error ? error : new Error('Failed to render PDF page')
        );
      }
    };

    const renderPromise = runRender();
    renderLockRef.current = renderPromise;

    void renderPromise.finally((): void => {
      if (renderLockRef.current === renderPromise) {
        renderLockRef.current = null;
      }
    });
  }, [containerSize.height, containerSize.width, currentPage, pdfDoc, scaleMode, zoom]);

  useEffect((): void => {
    renderPage();
  }, [renderPage]);

  useEffect((): (() => void) => {
    return (): void => {
      renderVersionRef.current += 1;

      if (resizeDebounceTimeoutRef.current) {
        clearTimeout(resizeDebounceTimeoutRef.current);
        resizeDebounceTimeoutRef.current = null;
      }

      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, []);

  useEffect((): void => {
    if (totalPages > 0) {
      resetScrollPosition();
      setZoom(PDF_MIN_ZOOM);
      onPageChangeRef.current?.(currentPage, totalPages);
    }
  }, [currentPage, totalPages, resetScrollPosition]);

  useEffect((): void => {
    onZoomChangeRef.current?.(zoom);
  }, [zoom]);

  useImperativeHandle(
    ref,
    (): PdfViewerRef => ({
      goToPage: (page: number): void => {
        setInternalPage(Math.max(1, Math.min(page, totalPages)));
        setZoom(PDF_MIN_ZOOM);
      },
      prevPage: (): void => {
        setInternalPage((page: number) => Math.max(1, page - 1));
      },
      nextPage: (): void => {
        setInternalPage((page: number) => Math.min(totalPages, page + 1));
      },
      zoomIn: (): void => {
        setZoom((currentZoom: number) => clampZoom(currentZoom + PDF_ZOOM_STEP));
      },
      zoomOut: (): void => {
        setZoom((currentZoom: number) => clampZoom(currentZoom - PDF_ZOOM_STEP));
      },
      resetZoom: (): void => {
        setZoom(PDF_MIN_ZOOM);
      },
      getCurrentPage: (): number => currentPage,
      getTotalPages: (): number => totalPages,
      getZoom: (): number => zoom,
    }),
    [currentPage, totalPages, zoom]
  );

  const canvasStyle = (canvasNum: 1 | 2): CSSProperties => ({
    display: 'block',
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: visibleCanvas === canvasNum ? 1 : 0,
    transition: transitionDuration > 0 ? `opacity ${transitionDuration}ms ease-in-out` : 'none',
  });

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex',
        scaleMode === 'fit-width'
          ? 'items-start justify-start overflow-auto'
          : 'items-center justify-center overflow-hidden',
        className
      )}
      style={{ width: '100%', height: '100%', touchAction: 'pan-x pan-y' }}
    >
      <div
        className="relative shrink-0"
        style={{
          width: pageSize.width,
          height: pageSize.height,
        }}
      >
        <canvas ref={canvas1Ref} style={canvasStyle(1)} />
        <canvas ref={canvas2Ref} style={canvasStyle(2)} />
      </div>
    </div>
  );
});

PdfViewer.displayName = 'PdfViewer';
